/**
 * Validation utility functions for form fields
 * These return error keys for i18n translation, not hardcoded messages
 */

/**
 * Generic ID/Document number validation
 * Validates that an ID contains only alphanumeric characters and meets minimum length
 * @param {string} id - ID/Document number
 * @param {number} minLength - Minimum length (default: 5)
 * @param {number} maxLength - Maximum length (default: 20)
 * @param {Function} t - Translation function (optional)
 * @returns {string|null} - Error message or null if valid
 */
export const validateIdNumber = (id, minLength = 5, maxLength = 20, t = null) => {
  if (!id) {
    return t ? t('validation.idRequired') : 'נא להזין מספר מזהה';
  }

  // Remove spaces and dashes for validation
  const cleaned = id.replace(/[\s-]/g, '');

  if (cleaned.length < minLength) {
    return t
      ? t('validation.idMinLength', { min: minLength })
      : `מספר מזהה חייב להכיל לפחות ${minLength} תווים`;
  }

  if (cleaned.length > maxLength) {
    return t
      ? t('validation.idMaxLength', { max: maxLength })
      : `מספר מזהה לא יכול להכיל יותר מ-${maxLength} תווים`;
  }

  // Allow alphanumeric characters only
  if (!/^[a-zA-Z0-9]+$/.test(cleaned)) {
    return t ? t('validation.idAlphanumeric') : 'מספר מזהה יכול להכיל רק אותיות ומספרים';
  }

  return null; // Valid
};

/**
 * International phone number validation
 * Validates phone numbers with international format support
 * @param {string} phone - Phone number
 * @param {Function} t - Translation function (optional)
 * @returns {string|null} - Error message or null if valid
 */
export const validatePhone = (phone, t = null) => {
  if (!phone) {
    return t ? t('validation.phoneRequired') : 'נא להזין מספר טלפון';
  }

  // Remove spaces, dashes, and parentheses
  const cleaned = phone.replace(/[\s\-()]/g, '');

  // Allow international format with + prefix or without
  // Minimum 7 digits, maximum 15 (E.164 standard)
  if (!/^\+?\d{7,15}$/.test(cleaned)) {
    return t ? t('validation.phoneInvalid') : 'מספר טלפון לא תקין. יש להזין 7-15 ספרות';
  }

  return null;
};

/**
 * Email validation
 * @param {string} email - Email address
 * @returns {string|null} - Error message or null if valid
 */
export const validateEmail = (email) => {
  if (!email) return 'נא להזין כתובת אימייל';

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return 'כתובת אימייל אינה תקינה';
  }

  return null;
};

/**
 * Name validation (Hebrew or English letters only)
 * @param {string} name - Name field
 * @param {number} minLength - Minimum length (default: 2)
 * @returns {string|null} - Error message or null if valid
 */
export const validateName = (name, minLength = 2) => {
  if (!name) return 'שדה חובה';

  if (name.trim().length < minLength) {
    return `יש להזין לפחות ${minLength} תווים`;
  }

  // Hebrew letters, English letters, spaces, and hyphens
  if (!/^[\u0590-\u05FFa-zA-Z\s-]+$/.test(name)) {
    return 'יש להזין אותיות בעברית או אנגלית בלבד';
  }

  return null;
};

/**
 * Required field validation
 * @param {string} value - Field value
 * @param {string} fieldName - Field name for error message
 * @returns {string|null} - Error message or null if valid
 */
export const validateRequired = (value, fieldName = 'שדה') => {
  if (!value || value.trim() === '') {
    return `${fieldName} הוא שדה חובה`;
  }
  return null;
};

/**
 * Date validation
 * @param {string} date - Date string
 * @param {boolean} allowFuture - Allow future dates (default: false)
 * @returns {string|null} - Error message or null if valid
 */
export const validateDate = (date, allowFuture = false) => {
  if (!date) return 'נא להזין תאריך';

  const dateObj = new Date(date);

  if (isNaN(dateObj.getTime())) {
    return 'תאריך לא תקין';
  }

  if (!allowFuture && dateObj > new Date()) {
    return 'לא ניתן להזין תאריך עתידי';
  }

  return null;
};

/**
 * Number validation
 * @param {string} value - Number value
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {string|null} - Error message or null if valid
 */
export const validateNumber = (value, min = null, max = null) => {
  if (!value) return 'נא להזין מספר';

  const num = parseFloat(value);

  if (isNaN(num)) {
    return 'יש להזין מספר תקין';
  }

  if (min !== null && num < min) {
    return `הערך חייב להיות לפחות ${min}`;
  }

  if (max !== null && num > max) {
    return `הערך חייב להיות מקסימום ${max}`;
  }

  return null;
};

/**
 * Length validation
 * @param {string} value - String value
 * @param {number} minLength - Minimum length
 * @param {number} maxLength - Maximum length
 * @returns {string|null} - Error message or null if valid
 */
export const validateLength = (value, minLength = null, maxLength = null) => {
  if (!value) return null; // Use validateRequired separately

  const length = value.length;

  if (minLength !== null && length < minLength) {
    return `יש להזין לפחות ${minLength} תווים`;
  }

  if (maxLength !== null && length > maxLength) {
    return `מקסימום ${maxLength} תווים`;
  }

  return null;
};

/**
 * Get validator function based on field type
 * @param {string} fieldType - Field type from constants
 * @returns {Function|null} - Validator function or null
 */
export const getValidatorForFieldType = (fieldType) => {
  const validators = {
    // ID fields - generic validation
    childId: (value) => validateIdNumber(value, 5, 20),
    parentId1: (value) => validateIdNumber(value, 5, 20),
    parentId2: (value) => validateIdNumber(value, 5, 20),

    // Phone numbers - international format
    phoneNumber1: validatePhone,
    phoneNumber2: validatePhone,
    emergencyContactNumber: validatePhone,
    parentWorkPhoneNumber1: validatePhone,
    parentWorkPhoneNumber2: validatePhone,

    // Email addresses
    parentEmailAddress1: validateEmail,
    parentEmailAddress2: validateEmail,

    // Names - require minimum 2 characters
    childName: (value) => validateName(value, 2),
    childFirstName: (value) => validateName(value, 2),
    childLastName: (value) => validateName(value, 2),
    parentName1: (value) => validateName(value, 2),
    parentName2: (value) => validateName(value, 2),
    emergencyContactName: (value) => validateName(value, 2),

    // Dates
    dateOfBirth: (value) => validateDate(value, false),
    signingDate: (value) => validateDate(value, true),
    attendanceStartingDate: (value) => validateDate(value, true),
  };

  return validators[fieldType] || null;
};

/**
 * Validation messages library
 */
export const validationMessages = {
  required: 'שדה חובה',
  minLength: (min) => `יש להזין לפחות ${min} תווים`,
  maxLength: (max) => `מקסימום ${max} תווים`,
  invalidId: 'מספר תעודת זהות אינו תקין',
  invalidPhone: 'מספר טלפון לא תקין. פורמט: 050-1234567',
  invalidEmail: 'כתובת אימייל אינה תקינה',
  invalidDate: 'תאריך לא תקין',
  futureDateNotAllowed: 'לא ניתן להזין תאריך עתידי',
  onlyLetters: 'יש להזין אותיות בלבד',
  onlyNumbers: 'יש להזין מספרים בלבד',
  fileTooBig: (maxMB) => `גודל הקובץ חורג מ-${maxMB}MB`,
  invalidFileType: 'סוג קובץ לא נתמך',
  success: 'נראה טוב!',
};
